/**
 * Copy `text` to the clipboard. Resolves true on success, false on
 * failure. Falls back to a transient `<textarea>` + `execCommand`
 * when `navigator.clipboard` is unavailable (insecure context, older
 * browsers, jsdom).
 */
export async function copyText(text: string): Promise<boolean> {
  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard?.writeText
  ) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // fall through to the legacy path below
    }
  }
  if (typeof document === "undefined") return false

  // Host the textarea beside whatever currently holds focus, NOT on <body>.
  //
  // An open Radix menu or dialog renders inside a `FocusScope` with
  // `trapped` (react-menu passes `trapFocus: context.open` for a modal
  // content, which is the default). Focusing a node *outside* that scope
  // makes the trap pull focus straight back, so by the time
  // `execCommand("copy")` runs the textarea no longer holds focus and the
  // document selection is empty. It copies nothing — and still answers
  // true, so the caller reports success over an untouched clipboard. (The
  // textarea's own range is NOT collapsed; see the focus check below for
  // why that matters.)
  //
  // That is QA CPLG-010: all three of the invoice row menu's copy items
  // ("Copy payment link" / "Copy portal link" / "Copy receipt link") lied,
  // while their detail-page twins — same helper, no menu, no trap — worked.
  // Inside the scope the trap has nothing to correct.
  //
  // Measured, not inferred (a Playwright probe wrapping `execCommand` on the
  // live lane, same page and click, only the host differing): hosted on
  // <body> the document selection at copy time was "" ; hosted beside the
  // focused menu item it was the full URL. `execCommand` returned true both
  // times.
  const active = document.activeElement
  const host =
    active instanceof HTMLElement && active !== document.body
      ? (active.parentElement ?? document.body)
      : document.body

  const textarea = document.createElement("textarea")
  textarea.value = text
  textarea.setAttribute("readonly", "")
  // Kept out of the a11y tree and the tab order: the host may be an open
  // menu, whose items Radix collects and arrow-keys through.
  textarea.setAttribute("aria-hidden", "true")
  textarea.tabIndex = -1
  textarea.style.position = "fixed"
  textarea.style.opacity = "0"
  host.appendChild(textarea)

  let ok = false
  try {
    // Focus explicitly rather than relying on `select()` to do it as a side
    // effect: real browsers do, jsdom does not, and the check below is about
    // focus, so the code should state the precondition it then verifies.
    // `preventScroll` because the node is transient and off to one side.
    textarea.focus({ preventScroll: true })
    textarea.select()
    // `execCommand`'s own boolean is not sufficient: it answers true having
    // copied nothing. `execCommand("copy")` copies the selection of the
    // FOCUSED element, so believe it only if the textarea still holds focus at
    // the moment of the copy. Focus theft is precisely the CPLG-010 failure.
    //
    // Two tempting checks that do NOT work, both learned the hard way:
    //  - the textarea's own `selectionStart`/`selectionEnd`. They describe its
    //    internal range and survive a focus loss untouched — measured in
    //    Chromium under an open Radix menu, the body-hosted textarea reported
    //    `[0, 75]` on exactly the copy that copied nothing.
    //  - `document.getSelection().toString()`. It told the two cases apart in
    //    Chromium, but Firefox excludes form-control contents from the
    //    document Selection, so it would read "" on every SUCCESSFUL copy
    //    there and report "Could not copy" falsely.
    //
    // This is the difference between a rep seeing "Could not copy" and
    // pasting whatever was on their clipboard before into a message to a
    // customer — what made CPLG-010 a P1.
    const focused = document.activeElement === textarea
    ok = focused && document.execCommand("copy")
  } catch {
    ok = false
  }
  host.removeChild(textarea)
  // Give focus back: the trap would otherwise leave the menu with no active
  // item, and a dialog would lose its restore target.
  if (active instanceof HTMLElement) active.focus()
  return ok
}
